import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AnimatedCheck from '../components/AnimatedCheck';
import AnimatedError from '../components/AnimatedError';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MessageSquare, Loader2, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Estilos fixos para forçar tema claro
const lightThemeStyle = {
  '--background': '210 33% 98%',
  '--foreground': '220 15% 10%',
  '--card': '0 0% 100%',
  '--card-foreground': '220 15% 10%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '220 15% 10%',
  '--primary': '181 77% 24%',
  '--primary-foreground': '160 76% 97%',
  '--secondary': '210 25% 96%',
  '--secondary-foreground': '220 15% 15%',
  '--muted': '220 9% 46%',
  '--muted-foreground': '220 9% 46%',
  '--border': '214 17% 91%',
  '--input': '214 17% 91%',
  '--ring': '181 77% 24%',
};

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token/${token}`);
      if (response.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao redefinir senha');
      }

      setSuccess(true);
      toast.success('Senha alterada com sucesso!');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-teal-50 to-white p-4" style={lightThemeStyle}>
        <Card className="w-full max-w-md bg-white">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-teal-700 mb-4" />
            <p className="text-gray-500">Verificando token...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-teal-50 to-white p-4" style={lightThemeStyle}>
        <Card className="w-full max-w-md bg-white">
          <CardHeader className="text-center">
            <AnimatedError />
            <CardTitle className="text-2xl text-gray-900">Link Inválido</CardTitle>
            <CardDescription className="text-gray-500">
              Este link de recuperação é inválido ou expirou.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login')} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-teal-50 to-white p-4" style={lightThemeStyle}>
        <Card className="w-full max-w-md bg-white">
          <CardHeader className="text-center">
            <AnimatedCheck />
            <CardTitle className="text-2xl text-gray-900">Senha Alterada!</CardTitle>
            <CardDescription className="text-gray-500">
              Sua senha foi redefinida com sucesso. Você será redirecionado para o login...
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login')} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-teal-50 to-white p-4" style={lightThemeStyle}>
      <Card className="w-full max-w-md bg-white" data-testid="reset-password-form">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-xl bg-teal-700 flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-gray-900">Nova Senha</CardTitle>
          <CardDescription className="text-gray-500">Digite sua nova senha abaixo</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Nova Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Redefinir Senha'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
